import { buildLoginUrl } from '@thinkalpha/ui-core/loginUrl';
import { injectable, inject } from 'src/features/ioc';
import { managedSessionLifecycle } from 'src/lib/featureFlags/flags/managedSessionLifecycle';
import type { SessionMonitorService } from 'src/services/SessionMonitorService';
import type { ReactBindings } from 'src/types/bindings';

const DEFAULT_POPUP_CHECK_INTERVAL_MS = 1000;
const POPUP_WIDTH = 800;
const POPUP_HEIGHT = 600;

/**
 * Service that orchestrates re-authentication when a session expires.
 *
 * This service handles:
 * - Opening a login popup window (or falling back to same-window redirect)
 * - Monitoring the popup for completion
 * - Signaling session restoration across tabs
 */
@injectable()
export class ReauthenticationService {
    #isReauthenticating = false;
    #log: ReactBindings['Logger'];
    #loginWindow: Window | null = null;
    #checkIntervalId: ReturnType<typeof setTimeout> | null = null;

    constructor(
        @inject('SessionMonitorService')
        private readonly sessionMonitorService: SessionMonitorService,
        @inject('FeatureFlagService')
        private readonly featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('Logger') logger: ReactBindings['Logger'],
    ) {
        this.#log = logger.getSubLogger({ name: 'ReauthenticationService' }, { component: 'auth' });
    }

    /**
     * Returns whether a re-authentication flow is currently in progress.
     */
    isReauthenticating(): boolean {
        return this.#isReauthenticating;
    }

    /**
     * Starts the re-authentication flow.
     *
     * For managed OAuth2 services, opens a login popup to preserve the user's
     * current work. Falls back to same-window redirect if popup is blocked.
     *
     * For legacy Keycloak services, redirects directly to the login page.
     *
     * @param returnTo - Optional URL to return to after login. Defaults to current URL.
     */
    async startReauthentication(returnTo?: string): Promise<void> {
        if (this.#isReauthenticating) {
            if (TRACE) {
                this.#log.trace('Re-authentication already in progress, ignoring duplicate request');
            }
            return;
        }

        this.#isReauthenticating = true;

        try {
            if (!this.featureFlagService.getSignal(managedSessionLifecycle).value) {
                // Managed OAuth features disabled - redirect directly to login
                this.#redirectToLogin();
                return;
            }

            // For managed OAuth features, open login in new tab to preserve current work
            const targetUrl = returnTo ?? window.location.href;
            const loginUrl = buildLoginUrl({ returnTo: targetUrl });

            this.#openLoginPopup(loginUrl);
        } catch (error) {
            this.#log.error('Failed to start re-authentication', { error });
            this.#isReauthenticating = false;
            // Fallback to login page
            this.#redirectToLogin();
        }
    }

    /**
     * Cancels any in-progress re-authentication monitoring.
     * Call this when the component using this service unmounts.
     */
    cancelReauthentication(): void {
        this.#stopMonitoring();
        this.#isReauthenticating = false;
    }

    #openLoginPopup(loginUrl: string): void {
        // Calculate centered position for popup
        const left = Math.max(0, (window.screen.width - POPUP_WIDTH) / 2);
        const top = Math.max(0, (window.screen.height - POPUP_HEIGHT) / 2);

        const popupFeatures = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`;

        this.#loginWindow = window.open(loginUrl, '_blank', popupFeatures);

        if (this.#loginWindow) {
            if (TRACE) {
                this.#log.trace('Login popup opened successfully');
            }
            this.#startMonitoringPopup();
        } else {
            // Popup blocked, fallback to same-window redirect
            this.#log.warn('Login popup was blocked, falling back to same-window redirect');
            this.#isReauthenticating = false;
            window.location.href = loginUrl;
        }
    }

    #startMonitoringPopup(): void {
        // Clear any existing interval (but don't null the window reference)
        if (this.#checkIntervalId !== null) {
            clearTimeout(this.#checkIntervalId);
            this.#checkIntervalId = null;
        }

        const checkLoginCompletion = () => {
            try {
                // Check if the window is closed (user completed login or cancelled)
                if (!this.#loginWindow || this.#loginWindow.closed) {
                    if (TRACE) {
                        this.#log.trace('Login popup closed, signaling session restoration');
                    }

                    // Signal potential session restoration for cross-tab detection
                    this.sessionMonitorService.signalSessionRestoration();

                    this.#stopMonitoring();
                    this.#isReauthenticating = false;
                    return;
                }

                // Continue checking
                this.#checkIntervalId = setTimeout(checkLoginCompletion, DEFAULT_POPUP_CHECK_INTERVAL_MS);
            } catch (_error) {
                // Window access might be blocked by CORS, that's expected
                // Continue monitoring
                this.#checkIntervalId = setTimeout(checkLoginCompletion, DEFAULT_POPUP_CHECK_INTERVAL_MS);
            }
        };

        checkLoginCompletion();
    }

    #stopMonitoring(): void {
        if (this.#checkIntervalId !== null) {
            clearTimeout(this.#checkIntervalId);
            this.#checkIntervalId = null;
        }
        this.#loginWindow = null;
    }

    #redirectToLogin(): void {
        window.location.href = buildLoginUrl();
    }
}
